import { ChakraProvider } from '@chakra-ui/react';
import { Analytics } from '@vercel/analytics/react';
import { DefaultSeo } from 'next-seo';
import { AppProps } from 'next/app';
import { Inter, JetBrains_Mono, Outfit } from 'next/font/google';
import { Router } from 'next/router';
import NextNProgress from 'nextjs-progressbar';
import { QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';

import { queryClient } from '$services/query-client';
import { useUIStore } from '$stores/ui';
import { lightPalette } from '$styles/color-theme';
import { theme } from '$styles/theme';

import SEO from 'next-seo.config';

const inter = Inter({
  weight: ['400', '500', '600'],
  subsets: ['latin'],
  variable: '--font-body',
  display: 'swap',
});

const outfit = Outfit({
  weight: ['400', '500', '600', '700', '800'],
  subsets: ['latin'],
  variable: '--font-display',
  display: 'swap',
});

const jetBrainsMono = JetBrains_Mono({
  weight: ['400', '500'],
  subsets: ['latin'],
  variable: '--font-mono',
  display: 'swap',
});

function App({ Component, pageProps }: AppProps) {
  const { closeSidebar } = useUIStore();

  Router.events.on('routeChangeComplete', () => {
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
    closeSidebar();
  });

  return (
    <>
      <style jsx global>{`
        :root {
          --font-display: ${outfit.style.fontFamily};
          --font-body: ${inter.style.fontFamily};
          --font-mono: ${jetBrainsMono.style.fontFamily};
        }
        @media (prefers-reduced-motion: no-preference) {
          .fade-in-up {
            opacity: 0;
            transform: translateY(12px);
            transition: opacity 0.65s ease, transform 0.65s ease;
          }
          .fade-in-up.is-visible {
            opacity: 1;
            transform: translateY(0);
          }
          .spin-slow {
            animation: spin-slow 14s linear infinite;
          }
          @keyframes spin-slow {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
          .blob-morph {
            animation: blobMorph 12s ease-in-out infinite alternate;
          }
          @keyframes blobMorph {
            0% {
              border-radius: 52% 48% 62% 38% / 48% 56% 44% 52%;
            }
            50% {
              border-radius: 38% 62% 45% 55% / 60% 42% 58% 40%;
            }
            100% {
              border-radius: 60% 40% 38% 62% / 42% 58% 42% 58%;
            }
          }
          .float-slow {
            animation: floatY 6s ease-in-out infinite;
          }
          .float-slower {
            animation: floatY 8s ease-in-out infinite reverse;
          }
          @keyframes floatY {
            0%,
            100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-8px);
            }
          }
        }
      `}</style>

      <div
        className={`${outfit.variable} ${inter.variable} ${jetBrainsMono.variable}`}
      >
        <DefaultSeo {...SEO} />

        <NextNProgress
          color={lightPalette.accent}
          startPosition={0.3}
          stopDelayMs={200}
          height={2}
          options={{ showSpinner: false }}
        />

        <ChakraProvider theme={theme}>
          <QueryClientProvider client={queryClient}>
            <Component {...pageProps} />
            <Analytics />
            <ReactQueryDevtools />
          </QueryClientProvider>
        </ChakraProvider>
      </div>
    </>
  );
}

export default App;
